/**
 * Pure URL and prefill logic shared by the paste composer and the prompt
 * injection visualizer (src/scripts/tools/paste-create.ts and
 * prompt-injection.ts).
 *
 * Everything here is framework-free so it can be unit-tested. Navigation
 * targets are derived from live site data and resolved against an allowlist,
 * and lab prefill is only ever read from the current page's own URL (same
 * origin).
 */

export interface OpenRouteInputs {
  navigation: ReadonlyArray<{ label: string; href: string }>;
  tools: ReadonlyArray<{ id: string; href: string }>;
  series: ReadonlyArray<{ slug: string }>;
  social: { github: string; gitlab: string; linkedin: string };
}

/** Named attack presets the `inject` command can load by name. */
export const INJECT_PRESETS = [
  'ignore',
  'roleplay',
  'delimiter',
  'extraction',
  'encoding',
  'indirect',
] as const;

/**
 * Upper bound on prefilled lab input read from a URL. Defense in depth against
 * a pathologically long crafted link; ordinary prefills are far shorter.
 */
export const MAX_PREFILL_LENGTH = 8192;

/**
 * Build the lowercase keyword -> URL allowlist for the `open` command from live
 * site data, so the launcher never drifts from the real content.
 */
export function buildOpenRoutes(inputs: OpenRouteInputs): Record<string, string> {
  const routes: Record<string, string> = {
    home: '/',
    resume: '/resume/',
    github: inputs.social.github,
    gitlab: inputs.social.gitlab,
    linkedin: inputs.social.linkedin,
  };
  for (const item of inputs.navigation) {
    routes[item.label.toLowerCase()] = item.href;
  }
  for (const tool of inputs.tools) {
    routes[`lab/${tool.id}`] = tool.href;
  }
  for (const series of inputs.series) {
    routes[`series/${series.slug}`] = `/tutorials/paths/${series.slug}/`;
  }
  return routes;
}

/**
 * Resolve an `open <arg>` argument against the allowlist. Returns the target URL
 * or null if it is not a known destination. Only allowlisted keys resolve, so a
 * bare or hostile string cannot be turned into an arbitrary redirect.
 */
export function resolveOpenTarget(
  raw: string,
  routes: Record<string, string>
): string | null {
  const key = raw
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (!key) return null;
  return routes[key] ?? null;
}

/** Strip a single matching pair of wrapping quotes from a command argument. */
export function stripWrappingQuotes(value: string): string {
  return value.replace(/^["']/, '').replace(/["']$/, '');
}

/**
 * Build the URL for `paste "<text>"`. The text is carried in the fragment (not
 * the query string) so the draft never reaches the server.
 */
export function buildPasteUrl(arg: string): string {
  const text = stripWrappingQuotes(arg.trim());
  if (!text) return '/lab/paste/';
  return `/lab/paste/#${new URLSearchParams({ text }).toString()}`;
}

/**
 * Build the URL for `inject <payload|preset>`. A known preset name loads that
 * named example; anything else is treated as a literal payload.
 */
export function buildInjectUrl(arg: string): string {
  const value = stripWrappingQuotes(arg.trim());
  if (!value) return '/lab/prompt-injection/';
  if ((INJECT_PRESETS as readonly string[]).includes(value.toLowerCase())) {
    const qs = new URLSearchParams({ preset: value.toLowerCase() }).toString();
    return `/lab/prompt-injection/#${qs}`;
  }
  return `/lab/prompt-injection/#${new URLSearchParams({ payload: value }).toString()}`;
}

/**
 * Read prefill text for the paste composer from its own URL fragment. Returns
 * null when absent; capped at MAX_PREFILL_LENGTH.
 */
export function readPastePrefill(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const text = params.get('text');
  if (!text) return null;
  return text.slice(0, MAX_PREFILL_LENGTH);
}

/**
 * Resolve prefill for the prompt injection visualizer from its own URL. Prefers
 * a named preset, then a literal payload; the fragment takes precedence over the
 * query string. Returns null when there is nothing to prefill.
 */
export function resolveInjectionPrefill(
  hash: string,
  search: string,
  examples: Record<string, string>
): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, ''));
  const fromQuery = new URLSearchParams(search.replace(/^\?/, ''));
  const preset = fromHash.get('preset') ?? fromQuery.get('preset');
  const payload = fromHash.get('payload') ?? fromQuery.get('payload');

  if (preset && examples[preset]) {
    return examples[preset].slice(0, MAX_PREFILL_LENGTH);
  }
  if (payload) {
    return payload.slice(0, MAX_PREFILL_LENGTH);
  }
  return null;
}
